import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'

export interface PageMeta {
  title: string
  description?: string
  demo?: boolean
  actions?: ReactNode
  onExport?: () => void
  onSaveView?: () => void
  /** When set, renders a chevron-left link in the topbar that navigates to `to`. */
  back?: { label: string; to: string }
}

// Two separate contexts so that pages which only *set* meta (via useSetPageMeta)
// do not re-render every time `meta` changes. If both lived in one value object,
// each setMeta() would create a new value, re-render every consumer (including the
// page that just set it), produce a fresh `actions` element, re-run the effect and
// loop ("Maximum update depth exceeded"). The setter context's value is the stable
// useState setter, so setMeta() never re-renders the setter consumers.
const PageMetaValueContext = createContext<PageMeta | null>(null)
const SetPageMetaContext = createContext<(meta: PageMeta | null) => void>(() => {})

export function PageMetaProvider({ children }: { children: ReactNode }) {
  const [meta, setMeta] = useState<PageMeta | null>(null)

  return (
    <SetPageMetaContext.Provider value={setMeta}>
      <PageMetaValueContext.Provider value={meta}>
        {children}
      </PageMetaValueContext.Provider>
    </SetPageMetaContext.Provider>
  )
}

export function usePageMeta() {
  return useContext(PageMetaValueContext)
}

export function useSetPageMeta({ title, description, demo, actions, onExport, onSaveView, back }: PageMeta) {
  const setMeta = useContext(SetPageMetaContext)

  useEffect(() => {
    setMeta({ title, description, demo, actions, onExport, onSaveView, back })
  }, [title, description, demo, actions, onExport, onSaveView, back, setMeta])

  useEffect(() => {
    return () => setMeta(null)
  }, [setMeta])
}
